import { useEffect, useState } from 'react';
import { Sparkles, Zap } from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

export default function AiUsageCard() {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null);
  const [savingAuto, setSavingAuto] = useState(false);

  const load = async () => {
    try {
      const r = await api.get('/api/ai/usage');
      setUsage(r.data);
    } catch {
      setUsage(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-700/50 bg-slate-800/40 p-5 text-slate-500 text-sm">
        Loading AI usage…
      </div>
    );
  }

  if (!usage) return null;

  const buyPack = async (packId) => {
    setBuying(packId);
    try {
      const r = await api.post('/api/billing/ai-packs/checkout', { pack: packId });
      const url = r.data?.checkout_url || r.data?.url;
      if (url) {
        window.location.href = url;
        return;
      }
      toast.error('Could not start checkout');
    } catch {
      toast.error('Checkout failed. Try again or contact support.');
    } finally {
      setBuying(null);
    }
  };

  const toggleAuto = async (enabled) => {
    setSavingAuto(true);
    try {
      const pack = usage.autoTopupPack || '100';
      const r = await api.post('/api/ai/usage/auto-topup', {
        enabled,
        pack,
      });
      setUsage(r.data);
      toast.success(enabled ? 'Auto top-up enabled' : 'Auto top-up disabled');
    } catch {
      toast.error('Could not update auto top-up');
    } finally {
      setSavingAuto(false);
    }
  };

  const setPack = async (pack) => {
    setSavingAuto(true);
    try {
      const r = await api.post('/api/ai/usage/auto-topup', {
        enabled: !!usage.autoTopupEnabled,
        pack,
      });
      setUsage(r.data);
    } catch {
      toast.error('Could not update pack');
    } finally {
      setSavingAuto(false);
    }
  };

  const lowIncluded =
    !usage.unmetered
    && usage.includedRemaining != null
    && usage.includedRemaining < 5;

  return (
    <div className="rounded-2xl border border-violet-500/20 bg-violet-500/5 p-5 space-y-4">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl border border-violet-500/30 bg-violet-500/10 flex items-center justify-center shrink-0">
          <Sparkles size={18} className="text-violet-400" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-semibold text-white">AI analyses</h2>
          <p className="text-slate-400 text-sm mt-0.5">
            Desk tools stay unlimited. Claude property AI uses your monthly included runs, then purchased credits.
          </p>
        </div>
      </div>

      {usage.unmetered ? (
        <p className="text-sm text-violet-300">Unlimited on this account.</p>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-xl border border-slate-700/60 bg-slate-900/40 px-3 py-2.5">
            <p className="text-[11px] uppercase tracking-wide text-slate-500">Included left</p>
            <p className={`text-xl font-semibold ${lowIncluded ? 'text-amber-300' : 'text-white'}`}>
              {usage.includedRemaining ?? 0}
              <span className="text-slate-500 text-sm font-normal">
                {' '}/ {usage.includedMonthly ?? 0}
              </span>
            </p>
          </div>
          <div className="rounded-xl border border-slate-700/60 bg-slate-900/40 px-3 py-2.5">
            <p className="text-[11px] uppercase tracking-wide text-slate-500">Credit balance</p>
            <p className="text-xl font-semibold text-white">{usage.creditBalance ?? 0}</p>
          </div>
        </div>
      )}

      {lowIncluded && (
        <p className="text-xs text-amber-300/90 border border-amber-500/20 bg-amber-500/10 rounded-lg px-3 py-2">
          Under 5 included AI analyses left this period. Top up to keep re-analysing properties.
        </p>
      )}

      {!usage.unmetered && (
        <>
          <div>
            <p className="text-xs text-slate-400 mb-2">Buy credits</p>
            <div className="flex flex-wrap gap-2">
              {(usage.packs || []).map((p) => (
                <button
                  key={p.id}
                  type="button"
                  disabled={!!buying}
                  onClick={() => buyPack(p.id)}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-violet-600/80 hover:bg-violet-500 text-white text-sm px-3 py-2 disabled:opacity-50"
                >
                  <Zap size={14} />
                  {buying === p.id ? 'Opening…' : `${p.credits} · ${p.price}`}
                </button>
              ))}
            </div>
          </div>

          <div className="border-t border-slate-700/50 pt-3 space-y-2">
            <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
              <input
                type="checkbox"
                checked={!!usage.autoTopupEnabled}
                disabled={savingAuto}
                onChange={(e) => toggleAuto(e.target.checked)}
                className="rounded border-slate-600"
              />
              Auto top-up when credits run out
            </label>
            {usage.autoTopupEnabled && (
              <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400">
                <span>Pack:</span>
                {(usage.packs || []).map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    disabled={savingAuto}
                    onClick={() => setPack(p.id)}
                    className={`rounded-md px-2 py-1 border ${
                      usage.autoTopupPack === p.id
                        ? 'border-violet-400 text-violet-300'
                        : 'border-slate-700 text-slate-400 hover:border-slate-500'
                    }`}
                  >
                    {p.credits}
                  </button>
                ))}
                {!usage.hasPaymentMethod && (
                  <span className="text-amber-400">
                    Buy a pack once so we can save a card for auto top-up.
                  </span>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
